/**
 * Composable backdrop layer library for the Adaptive Reveal Engine.
 *
 * Every layer is a self-contained, GPU-only decoration:
 *   - position: absolute inset-0
 *   - pointer-events: none
 *   - transform / opacity / filter only
 *   - count seeded via the rng so two reveals never look identical
 *
 * All layers honour `density` (0–1.4) and accept the active palette.
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { CinematicPalette } from '../themePalettes';
import type { SeededRng } from '../adaptive/revealSeed';
import type { BackdropLayerId } from '../adaptive/revealStyles';

interface LayerProps {
  palette: CinematicPalette;
  rng: SeededRng;
  density?: number;
  /** Reduced count target on mobile. */
  isMobile?: boolean;
  hueShift?: number;
}

const EASE = [0.22, 0.61, 0.36, 1] as const;

/* ---------------- FogLayer ---------------- */
export function FogLayer({ palette, rng, hueShift = 0 }: LayerProps) {
  const blobs = useMemo(
    () => Array.from({ length: 3 }, (_, i) => ({
      x: rng.int(20, 80),
      y: rng.int(20, 80),
      size: 70 + rng.int(0, 50),
      delay: i * 2 + rng.next() * 2,
      duration: 18 + rng.int(0, 10),
    })),
    [rng],
  );
  return (
    <>
      {blobs.map((b, i) => (
        <motion.div
          key={`fog-${i}`}
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: `${b.size}%`,
            height: `${b.size}%`,
            left: `${b.x - b.size / 2}%`,
            top: `${b.y - b.size / 2}%`,
            background: `radial-gradient(ellipse at center, ${palette.light} 0%, transparent 60%)`,
            filter: `blur(50px) hue-rotate(${hueShift}deg)`,
            willChange: 'transform, opacity',
          }}
          animate={{
            x: [0, 18, -12, 0],
            y: [0, -10, 12, 0],
            opacity: [0.4, 0.75, 0.55, 0.4],
          }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </>
  );
}

/* ---------------- DustLayer ---------------- */
export function DustLayer({ palette, rng, density = 1, isMobile, hueShift = 0 }: LayerProps) {
  const dust = useMemo(() => {
    const count = Math.round((isMobile ? 12 : 22) * density);
    return Array.from({ length: count }, (_, i) => ({
      x: rng.next() * 100,
      y: rng.next() * 100,
      size: 1 + rng.next() * 2.5,
      duration: 7 + rng.next() * 6,
      delay: rng.next() * 4,
      driftX: (rng.next() - 0.5) * 30,
      driftY: -20 - rng.next() * 30,
      opacity: 0.25 + rng.next() * 0.5,
      key: `dust-${i}`,
    }));
  }, [rng, density, isMobile]);

  return (
    <div style={{ filter: hueShift ? `hue-rotate(${hueShift}deg)` : undefined }}>
      {dust.map((d) => (
        <motion.div
          key={d.key}
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            width: d.size,
            height: d.size,
            left: `${d.x}%`,
            top: `${d.y}%`,
            background: palette.particle,
            boxShadow: `0 0 ${d.size * 4}px ${d.size}px ${palette.particle}`,
            willChange: 'transform, opacity',
          }}
          animate={{
            x: [0, d.driftX, 0],
            y: [0, d.driftY, 0],
            opacity: [0, d.opacity, 0],
          }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ---------------- EmberLayer ---------------- */
export function EmberLayer({ palette, rng, density = 1, isMobile }: LayerProps) {
  const embers = useMemo(() => {
    const count = Math.round((isMobile ? 8 : 14) * density);
    return Array.from({ length: count }, (_, i) => ({
      x: rng.next() * 100,
      size: 2 + rng.next() * 3,
      delay: rng.next() * 5,
      duration: 8 + rng.next() * 6,
      key: `ember-${i}`,
    }));
  }, [rng, density, isMobile]);

  return (
    <>
      {embers.map((e) => (
        <motion.div
          key={e.key}
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            width: e.size,
            height: e.size,
            left: `${e.x}%`,
            bottom: '-2%',
            background: palette.accent,
            boxShadow: `0 0 ${e.size * 5}px ${e.size * 2}px ${palette.accent}`,
            willChange: 'transform, opacity',
          }}
          animate={{
            y: ['0vh', '-110vh'],
            x: [0, (rng.next() - 0.5) * 60],
            opacity: [0, 0.9, 0],
          }}
          transition={{ duration: e.duration, delay: e.delay, repeat: Infinity, ease: 'easeOut' }}
        />
      ))}
    </>
  );
}

/* ---------------- BokehLayer ---------------- */
export function BokehLayer({ palette, rng, density = 1, isMobile, hueShift = 0 }: LayerProps) {
  const bokeh = useMemo(() => {
    const count = Math.round((isMobile ? 6 : 12) * density);
    return Array.from({ length: count }, (_, i) => ({
      x: rng.next() * 100,
      y: rng.next() * 100,
      size: 30 + rng.next() * 80,
      duration: 14 + rng.next() * 8,
      delay: rng.next() * 6,
      opacity: 0.12 + rng.next() * 0.18,
      key: `bokeh-${i}`,
    }));
  }, [rng, density, isMobile]);

  return (
    <div style={{ filter: hueShift ? `hue-rotate(${hueShift}deg)` : undefined }}>
      {bokeh.map((b) => (
        <motion.div
          key={b.key}
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            width: b.size,
            height: b.size,
            left: `${b.x}%`,
            top: `${b.y}%`,
            background: `radial-gradient(circle, ${palette.particle} 0%, transparent 70%)`,
            filter: 'blur(8px)',
            willChange: 'transform, opacity',
          }}
          animate={{
            scale: [0.9, 1.1, 0.9],
            opacity: [0, b.opacity, 0],
          }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ---------------- RainLayer ---------------- */
export function RainLayer({ palette, rng, density = 1, isMobile }: LayerProps) {
  const drops = useMemo(() => {
    const count = Math.round((isMobile ? 24 : 50) * density);
    return Array.from({ length: count }, (_, i) => ({
      x: rng.next() * 100,
      length: 12 + rng.next() * 26,
      duration: 0.7 + rng.next() * 0.8,
      delay: rng.next() * 3,
      opacity: 0.15 + rng.next() * 0.3,
      key: `rain-${i}`,
    }));
  }, [rng, density, isMobile]);

  return (
    <>
      {drops.map((d) => (
        <motion.div
          key={d.key}
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            width: 1,
            height: d.length,
            left: `${d.x}%`,
            top: '-5%',
            background: `linear-gradient(to bottom, transparent, ${palette.particle})`,
            willChange: 'transform, opacity',
          }}
          animate={{ y: ['0vh', '110vh'], opacity: [0, d.opacity, 0] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: 'linear' }}
        />
      ))}
    </>
  );
}

/* ---------------- LightStreaksLayer (the slow conic rays) ---------------- */
export function LightStreaksLayer({ palette, hueShift = 0 }: LayerProps) {
  return (
    <motion.div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        width: '180%',
        height: '180%',
        left: '-40%',
        top: '-40%',
        background: `conic-gradient(from 0deg at 50% 50%, transparent 0deg, ${palette.light} 30deg, transparent 80deg, transparent 180deg, ${palette.light} 220deg, transparent 280deg)`,
        mixBlendMode: 'screen',
        opacity: 0.32,
        filter: hueShift ? `hue-rotate(${hueShift}deg)` : undefined,
        willChange: 'transform',
      }}
      animate={{ rotate: [0, 360] }}
      transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
    />
  );
}

/* ---------------- VolumetricRaysLayer (god rays) ---------------- */
export function VolumetricRaysLayer({ palette, rng }: LayerProps) {
  const angle = useMemo(() => rng.int(-25, 25), [rng]);
  return (
    <motion.div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        inset: 0,
        background: `linear-gradient(${angle}deg, transparent 30%, ${palette.light} 50%, transparent 70%)`,
        mixBlendMode: 'screen',
        opacity: 0.4,
        filter: 'blur(30px)',
        willChange: 'transform, opacity',
      }}
      animate={{ opacity: [0.25, 0.5, 0.3, 0.45], scale: [1, 1.05, 1] }}
      transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

/* ---------------- FilmGrainLayer ---------------- */
export function FilmGrainLayer() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: 0.09,
        mixBlendMode: 'overlay',
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9 0 0 0 0 0.9 0 0 0 0 0.9 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }}
    />
  );
}

/* ---------------- PaperTextureLayer ---------------- */
export function PaperTextureLayer() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: 0.18,
        mixBlendMode: 'multiply',
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='p'><feTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.55 0 0 0 0 0.45 0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23p)'/></svg>\")",
      }}
    />
  );
}

/* ---------------- HolographicLayer ---------------- */
export function HolographicLayer({ palette, hueShift = 0 }: LayerProps) {
  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `linear-gradient(120deg, ${palette.light}, transparent 30%, ${palette.particle} 60%, transparent 90%)`,
        mixBlendMode: 'screen',
        opacity: 0.45,
        filter: `blur(20px) hue-rotate(${hueShift}deg)`,
        backgroundSize: '300% 300%',
        willChange: 'background-position, opacity',
      }}
      animate={{ backgroundPosition: ['0% 0%', '100% 50%', '0% 100%', '0% 0%'] }}
      transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

/* ---------------- AuroraLayer ---------------- */
export function AuroraLayer({ palette, hueShift = 0 }: LayerProps) {
  return (
    <motion.div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        inset: '-20%',
        background: `radial-gradient(ellipse at 30% 30%, ${palette.particle} 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, ${palette.light} 0%, transparent 55%)`,
        filter: `blur(60px) hue-rotate(${hueShift}deg)`,
        mixBlendMode: 'screen',
        opacity: 0.55,
        willChange: 'transform, opacity',
      }}
      animate={{ x: [0, 30, -25, 0], y: [0, -20, 20, 0], opacity: [0.4, 0.7, 0.5, 0.4] }}
      transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

/* ---------------- Vignette + base gradient (always rendered) ---------------- */
export function BaseGradientLayer({ palette }: { palette: CinematicPalette }) {
  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `linear-gradient(135deg, ${palette.bgFrom} 0%, ${palette.bgVia} 50%, ${palette.bgTo} 100%)`,
        backgroundSize: '200% 200%',
      }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
      transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

export function VignetteLayer({ palette }: { palette: CinematicPalette }) {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(ellipse at center, transparent 50%, ${palette.vignette} 100%)`,
      }}
    />
  );
}

/* ---------------- Composer ---------------- */

export const BACKDROP_REGISTRY: Record<BackdropLayerId, (props: LayerProps) => JSX.Element> = {
  fog: FogLayer,
  dust: DustLayer,
  ember: EmberLayer,
  rain: RainLayer,
  bokeh: BokehLayer,
  lightStreaks: LightStreaksLayer,
  volumetricRays: VolumetricRaysLayer,
  filmGrain: () => <FilmGrainLayer />,
  paperTexture: () => <PaperTextureLayer />,
  holographic: HolographicLayer,
  aurora: AuroraLayer,
};

export const _ease = EASE;
